import {
  type KeyboardEvent,
  type KeyboardEventHandler,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, ButtonVariation } from '../Button';
import { ChevronDirection, ChevronIcon } from '../Icon';
import { ASSET_PATHS } from '../../utils/assetPath';
import { normalizeTestId } from '../../utils';
import { ROUTES } from '../../constants';
import { useRBAC } from '../../hooks/useRBAC';
import { NAV_ITEMS, NAV_LABELS, NOVA_CHILDREN } from '../../pages/landingPage/constants';
import './TopNavigationBar.css';

interface TopNavigationBarProps {
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function TopNavigationBar({ 'data-testid': dataTestId }: TopNavigationBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { capabilities } = useRBAC();
  const [isNovaOpen, setIsNovaOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileNovaOpen, setIsMobileNovaOpen] = useState(false);
  const [isHamburgerHovered, setIsHamburgerHovered] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);
  const novaMenuRef = useRef<HTMLDivElement | null>(null);
  const novaButtonRef = useRef<HTMLButtonElement | null>(null);
  const novaItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [isNovaKeyboardOpen, setIsNovaKeyboardOpen] = useState(false);
  const primaryNavItems = NAV_ITEMS.slice(0, 3);
  const secondaryNavItems = capabilities.canAccessContracts ? NAV_ITEMS.slice(3) : [];
  const visibleNovaChildren = NOVA_CHILDREN.filter((child) => {
    if (child.path === ROUTES.NOVA_APPLICATION) return capabilities.canAccessApplication;
    if (child.path === ROUTES.NOVA_APPLICATION_STATUS) return capabilities.canAccessApplicationStatus;
    if (child.path === ROUTES.APPROVALS) return capabilities.canAccessApprovals;

    return false;
  });
  const showNova = visibleNovaChildren.length > 0;
  const pathname = location.pathname;
  const activeNovaChildPath = [...visibleNovaChildren]
    .sort((firstChild, secondChild) => secondChild.path.length - firstChild.path.length)
    .find((child) => pathname === child.path || pathname.startsWith(`${child.path}/`))?.path;
  const baseTestId = dataTestId ?? 'top-navigation-bar';
  const isHomeActive = pathname === ROUTES.HOME;
  const isProfileActive = pathname === ROUTES.MY_PROFILE || pathname.startsWith(ROUTES.MY_PROFILE);
  const isContractsActive = pathname.startsWith('/contract');
  const isNovaActive =
    pathname.startsWith('/nova') || pathname.startsWith('/application') || pathname.startsWith('/approvals');

  const isNavItemActive = (label: string) => {
    switch (label) {
      case NAV_LABELS.HOME:
        return isHomeActive;
      case NAV_LABELS.MY_PROFILE:
        return isProfileActive;
      case NAV_LABELS.MANAGE_CONTRACT:
        return isContractsActive;
      default:
        return false;
    }
  };

  const getNavItemClass = (label: string) =>
    `landing-page__auth-nav-button${isNavItemActive(label) ? ' landing-page__auth-nav-button--active' : ''}`;

  const handleNavigate = (path: string) => {
    setIsNovaOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileNovaOpen(false);

    if (path === '/application' && location.pathname === '/application') {
      navigate(path, { replace: true, state: { resetWorkflow: true } });
    } else {
      navigate(path);
    }
  };

  const handleExternalNavigate = () => {
    setIsNovaOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileNovaOpen(false);
  };

  const handleExternalMouseUp = (event: ReactMouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.currentTarget.blur();
  };

  const handleOpenNova = () => {
    setIsNovaOpen(true);
  };

  const handleCloseNova = () => {
    setIsNovaOpen(false);
  };

  const handleToggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    setIsMobileNovaOpen(false);
  };

  const handleToggleMobileNova = () => {
    setIsMobileNovaOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!navRef.current) {
        return;
      }

      if (!navRef.current.contains(event.target as Node)) {
        setIsNovaOpen(false);
        setIsNovaKeyboardOpen(false);
        setIsMobileMenuOpen(false);
        setIsMobileNovaOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    if (!isNovaOpen || !isNovaKeyboardOpen) {
      return;
    }

    const firstItem = novaItemRefs.current[0];
    if (firstItem) {
      firstItem.focus();
    }
  }, [isNovaOpen, isNovaKeyboardOpen]);

  const closeNovaMenu = () => {
    setIsNovaOpen(false);
    setIsNovaKeyboardOpen(false);
  };

  const handleNovaButtonKeyDown: KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsNovaOpen(true);
      setIsNovaKeyboardOpen(true);

      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsNovaOpen(true);
      setIsNovaKeyboardOpen(true);

      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeNovaMenu();
    }
  };

  const handleNovaMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = novaItemRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (items.length === 0) {
      return;
    }

    const currentIndex = items.findIndex((item) => item === document.activeElement);
    const focusItem = (index: number) => {
      const next = items[index];
      if (next) {
        next.focus();
      }
    };

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
        focusItem(nextIndex);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
        focusItem(nextIndex);
        break;
      }
      case 'Home': {
        event.preventDefault();
        focusItem(0);
        break;
      }
      case 'End': {
        event.preventDefault();
        focusItem(items.length - 1);
        break;
      }
      case 'Escape': {
        event.preventDefault();
        closeNovaMenu();
        novaButtonRef.current?.focus();
        break;
      }
      case 'Tab': {
        closeNovaMenu();
        break;
      }
      default:
        break;
    }
  };

  const handleNovaMenuBlur = () => {
    window.setTimeout(() => {
      const activeElement = document.activeElement;
      if (!novaMenuRef.current?.contains(activeElement) && activeElement !== novaButtonRef.current) {
        closeNovaMenu();
      }
    }, 0);
  };

  return (
    <nav
      ref={navRef}
      aria-label='Authenticated top navigation'
      className='landing-page__auth-nav'
      data-testid={baseTestId}>
      <div
        className='landing-page__auth-nav-inner ds-l-container'
        data-testid={`${baseTestId}-inner`}>
        <div
          className='landing-page__auth-nav-desktop'
          data-testid={`${baseTestId}-desktop`}>
          {primaryNavItems.map((item) => (
            <Button
              key={item.label}
              isAutoWidth
              className={getNavItemClass(item.label)}
              data-testid={`${baseTestId}-primary-${normalizeTestId(item.label)}`}
              href={item.isExternal ? item.path : undefined}
              rel={item.isExternal ? 'noopener noreferrer' : undefined}
              target={item.isExternal ? '_blank' : undefined}
              variation={ButtonVariation.GHOST}
              onClick={item.isExternal ? handleExternalNavigate : () => handleNavigate(item.path)}
              onMouseUp={item.isExternal ? handleExternalMouseUp : undefined}>
              {item.label === NAV_LABELS.HOME && (
                <img
                  alt=''
                  aria-hidden='true'
                  className='landing-page__auth-nav-icon'
                  src={isHomeActive ? ASSET_PATHS.HOME_ICON : ASSET_PATHS.HOME_INACTIVE_ICON}
                />
              )}
              {item.label}
            </Button>
          ))}
          {showNova && (
            <div
              className='landing-page__auth-nav-dropdown'
              data-testid={`${baseTestId}-nova-dropdown`}
              onBlur={handleNovaMenuBlur}
              onMouseEnter={handleOpenNova}
              onMouseLeave={handleCloseNova}>
              <Button
                ref={novaButtonRef}
                isAutoWidth
                aria-controls='nova-menu'
                aria-expanded={isNovaOpen}
                aria-haspopup='menu'
                className={`landing-page__auth-nav-button${
                  isNovaActive ? ' landing-page__auth-nav-button--active' : ''
                }${isNovaOpen ? ' landing-page__auth-nav-button--hover' : ''}`}
                data-testid={`${baseTestId}-nova-toggle`}
                variation={ButtonVariation.GHOST}
                onKeyDown={handleNovaButtonKeyDown}>
                {NAV_LABELS.NOVA}
              </Button>
              {isNovaOpen && (
                <div
                  ref={novaMenuRef}
                  className='landing-page__auth-nav-menu'
                  data-testid={`${baseTestId}-nova-menu`}
                  id='nova-menu'
                  role='menu'
                  onKeyDown={handleNovaMenuKeyDown}>
                  {visibleNovaChildren.map((child, index) => (
                    <button
                      key={child.label}
                      ref={(el) => {
                        novaItemRefs.current[index] = el;
                      }}
                      className='landing-page__auth-nav-menu-item'
                      data-testid={`${baseTestId}-nova-item-${normalizeTestId(child.label)}`}
                      role='menuitem'
                      tabIndex={-1}
                      type='button'
                      onClick={() => handleNavigate(child.path)}>
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {secondaryNavItems.map((item) => (
            <Button
              key={item.label}
              isAutoWidth
              className={getNavItemClass(item.label)}
              data-testid={`${baseTestId}-secondary-${normalizeTestId(item.label)}`}
              variation={ButtonVariation.GHOST}
              onClick={() => handleNavigate(item.path)}>
              {item.label}
            </Button>
          ))}
        </div>

        <div
          className='landing-page__auth-nav-mobile'
          data-testid={`${baseTestId}-mobile`}>
          <Button
            isAutoWidth
            className={`landing-page__auth-nav-button landing-page__auth-nav-home${
              isHomeActive ? ' landing-page__auth-nav-button--active' : ''
            }`}
            data-testid={`${baseTestId}-mobile-home`}
            variation={ButtonVariation.GHOST}
            onClick={() => handleNavigate(ROUTES.HOME)}>
            <img
              alt=''
              aria-hidden='true'
              className='landing-page__auth-nav-icon'
              src={isHomeActive ? ASSET_PATHS.HOME_ICON : ASSET_PATHS.HOME_INACTIVE_ICON}
            />
            {NAV_LABELS.HOME}
          </Button>
          <Button
            isIconOnly
            aria-expanded={isMobileMenuOpen}
            aria-haspopup='menu'
            className={`landing-page__auth-nav-button landing-page__auth-nav-hamburger${
              isMobileMenuOpen ? ' landing-page__auth-nav-hamburger--active' : ''
            }`}
            data-testid={`${baseTestId}-mobile-menu-toggle`}
            variation={ButtonVariation.GHOST}
            onClick={handleToggleMobileMenu}
            onMouseEnter={() => setIsHamburgerHovered(true)}
            onMouseLeave={() => setIsHamburgerHovered(false)}>
            <img
              alt='Open menu'
              src={
                isMobileMenuOpen || isHamburgerHovered
                  ? ASSET_PATHS.HAMBURGER_MENU_ICON
                  : ASSET_PATHS.HAMBURGER_MENU_ICON_INACTIVE
              }
            />
          </Button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          className='landing-page__auth-nav-mobile-menu'
          data-testid={`${baseTestId}-mobile-menu`}
          role='menu'>
          <div
            className='landing-page__auth-nav-mobile-header'
            data-testid={`${baseTestId}-mobile-header`}>
            <Button
              isIconOnly
              aria-label='Close menu'
              className='landing-page__auth-nav-button landing-page__auth-nav-close'
              data-testid={`${baseTestId}-mobile-close`}
              variation={ButtonVariation.GHOST}
              onClick={handleToggleMobileMenu}>
              <img
                alt=''
                aria-hidden='true'
                src={ASSET_PATHS.CLOSE_ICON}
              />
            </Button>
          </div>
          {[...primaryNavItems, ...secondaryNavItems].map((item) => (
            <Button
              key={item.label}
              isAutoWidth
              className={`landing-page__auth-nav-mobile-item${
                isNavItemActive(item.label) ? ' landing-page__auth-nav-button--active' : ''
              }`}
              data-testid={`${baseTestId}-mobile-item-${normalizeTestId(item.label)}`}
              href={item.isExternal ? item.path : undefined}
              rel={item.isExternal ? 'noopener noreferrer' : undefined}
              target={item.isExternal ? '_blank' : undefined}
              variation={ButtonVariation.GHOST}
              onClick={item.isExternal ? handleExternalNavigate : () => handleNavigate(item.path)}
              onMouseUp={item.isExternal ? handleExternalMouseUp : undefined}>
              {item.label}
            </Button>
          ))}
          {showNova && (
            <Button
              isAutoWidth
              aria-expanded={isMobileNovaOpen}
              aria-haspopup='menu'
              className={`landing-page__auth-nav-mobile-item${
                isMobileNovaOpen || isNovaActive ? ' landing-page__auth-nav-button--active' : ''
              }`}
              data-testid={`${baseTestId}-mobile-nova-toggle`}
              variation={ButtonVariation.GHOST}
              onClick={handleToggleMobileNova}>
              {NAV_LABELS.NOVA}
              <span aria-hidden='true'>
                <ChevronIcon
                  direction={isMobileNovaOpen ? ChevronDirection.UP : ChevronDirection.DOWN}
                  size={16}
                />
              </span>
            </Button>
          )}
          {showNova && isMobileNovaOpen && (
            <div
              className='landing-page__auth-nav-mobile-submenu'
              data-testid={`${baseTestId}-mobile-nova-menu`}>
              {visibleNovaChildren.map((child) => (
                <Button
                  key={child.label}
                  isAutoWidth
                  className={`landing-page__auth-nav-mobile-subitem${
                    activeNovaChildPath === child.path ? ' landing-page__auth-nav-button--active' : ''
                  }`}
                  data-testid={`${baseTestId}-mobile-nova-item-${normalizeTestId(child.label)}`}
                  variation={ButtonVariation.GHOST}
                  onClick={() => handleNavigate(child.path)}>
                  {child.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

export default TopNavigationBar;
